import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Switch,
  Typography,
  Chip,
  Tooltip,
  Pagination,
  InputAdornment,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import { get, post, put, del } from "../../config/axios";
import { toastMessage } from "../../utils/toastMessage";
import Layout from "../../layout/Main/Layout";

const PromptManagement = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [viewModal, setViewModal] = useState(false);
  const [viewingSession, setViewingSession] = useState(null);
  const [promptModal, setPromptModal] = useState(false);
  const [viewingPrompt, setViewingPrompt] = useState("");

  // Form state
  const [formData, setFormData] = useState({
    sessionName: "",
    sessionNumber: "",
    mediaAssetReference: "None",
    unlockConditions: "",
    completionCriteria: "",
    summaryInstruction: "",
    prompt: "",
    intendedGap: "NA",
    minGap: "NA",
    compliant: "NA",
    moderate: "NA",
    nonCompliant: "NA",
    isEnabled: true,
  });

  useEffect(() => {
    fetchSessions();
  }, [currentPage, searchTerm]);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const response = await get(
        `/api/dashboard/prompts?page=${currentPage}&limit=10&search=${searchTerm}`
      );

      if (response?.success) {
        setSessions(response.data || []);
        setTotalPages(response.pagination?.totalPages || 1);
      }
    } catch (error) {
      console.error("Error fetching sessions:", error);
      toastMessage("Failed to fetch sessions", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const data = {
        ...formData,
        sessionNumber: parseInt(formData.sessionNumber),
      };

      if (editingSession) {
        const response = await put(`/api/dashboard/prompts/${editingSession._id}`, data);
        if (response?.success) {
          toastMessage("Session updated successfully", "success");
        }
      } else {
        const response = await post("/api/dashboard/prompts", data);
        if (response?.success) {
          toastMessage("Session created successfully", "success");
        }
      }

      closeModal();
      fetchSessions();
    } catch (error) {
      console.error("Error saving session:", error);
      toastMessage(error.response?.data?.message || "Failed to save session", "error");
    }
  };

  const handleDelete = async (sessionId) => {
    if (!window.confirm("Are you sure you want to delete this session?")) return;

    try {
      const response = await del(`/api/dashboard/prompts/${sessionId}`);
      if (response?.success) {
        toastMessage("Session deleted successfully", "success");
        fetchSessions();
      }
    } catch (error) {
      console.error("Error deleting session:", error);
      toastMessage("Failed to delete session", "error");
    }
  };

  const handleToggleStatus = async (sessionId) => {
    try {
      const response = await put(`/api/dashboard/prompts/${sessionId}/toggle`);
      if (response?.success) {
        toastMessage(response.message, "success");
        fetchSessions();
      }
    } catch (error) {
      console.error("Error toggling session status:", error);
      toastMessage("Failed to toggle session status", "error");
    }
  };

  const openEditModal = (session) => {
    setEditingSession(session);
    setFormData({
      sessionName: session.sessionName || "",
      sessionNumber: session.sessionNumber?.toString() || "",
      mediaAssetReference: session.mediaAssetReference || "None",
      unlockConditions: session.unlockConditions || "",
      completionCriteria: session.completionCriteria || "",
      summaryInstruction: session.summaryInstruction || "",
      prompt: session.prompt || "",
      intendedGap: session.intendedGap || "NA",
      minGap: session.minGap || "NA",
      compliant: session.compliant || "NA",
      moderate: session.moderate || "NA",
      nonCompliant: session.nonCompliant || "NA",
      isEnabled: session.isEnabled !== undefined ? session.isEnabled : true,
    });
    setOpenModal(true);
  };

  const openViewModal = (session) => {
    setViewingSession(session);
    setViewModal(true);
  };

  const closeModal = () => {
    setOpenModal(false);
    setEditingSession(null);
    setFormData({
      sessionName: "",
      sessionNumber: "",
      mediaAssetReference: "None",
      unlockConditions: "",
      completionCriteria: "",
      summaryInstruction: "",
      prompt: "",
      intendedGap: "NA",
      minGap: "NA",
      compliant: "NA",
      moderate: "NA",
      nonCompliant: "NA",
      isEnabled: true,
    });
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleViewPrompt = (session) => {
    setViewingPrompt(session.prompt || "No prompt content");
    setPromptModal(true);
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Typography variant="h4" component="h1">
            Prompt Management
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenModal(true)}
          >
            Add Session
          </Button>
        </Box>

        {/* Search Bar */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            placeholder="Search sessions by name, unlock conditions, completion criteria, or prompt..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Sessions Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Session Name</TableCell>
                <TableCell>Session #</TableCell>
                <TableCell>Media Asset Reference</TableCell>
                <TableCell>Session Unlock Conditions</TableCell>
                <TableCell>Completion Criteria</TableCell>
                <TableCell>Session Summary Instruction</TableCell>
                <TableCell>Prompt</TableCell>
                <TableCell>Intended Gap</TableCell>
                <TableCell>Min Gap</TableCell>
                <TableCell>Compliant</TableCell>
                <TableCell>Moderate</TableCell>
                <TableCell>Non-Compliant</TableCell>
                <TableCell>Enable Toggle</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sessions.map((session) => (
                <TableRow key={session._id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {session.sessionName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={session.sessionNumber} color="primary" size="small" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {session.mediaAssetReference}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 200 }}>
                      {session.unlockConditions}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 200 }}>
                      {session.completionCriteria}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 200 }}>
                      {session.summaryInstruction}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<VisibilityIcon />}
                      onClick={() => handleViewPrompt(session)}
                    >
                      View Prompt
                    </Button>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {session.intendedGap}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {session.minGap}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {session.compliant}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {session.moderate}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {session.nonCompliant}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={session.isEnabled}
                      onChange={() => handleToggleStatus(session._id)}
                      color="primary"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => openViewModal(session)}
                          color="info"
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit Session">
                        <IconButton
                          size="small"
                          onClick={() => openEditModal(session)}
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Session">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(session._id)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(e, page) => setCurrentPage(page)}
              color="primary"
            />
          </Box>
        )}

        {/* Add/Edit Modal */}
        <Dialog open={openModal} onClose={closeModal} maxWidth="md" fullWidth>
          <DialogTitle>
            {editingSession ? "Edit CBT Session" : "Add New CBT Session"}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mt: 2 }}>
              <TextField
                label="Session Name"
                value={formData.sessionName}
                onChange={(e) => handleInputChange("sessionName", e.target.value)}
                fullWidth
                required
              />
              <TextField
                label="Session Number"
                type="number"
                value={formData.sessionNumber}
                onChange={(e) => handleInputChange("sessionNumber", e.target.value)}
                fullWidth
                required
              />
              <TextField
                label="Media Asset Reference"
                value={formData.mediaAssetReference}
                onChange={(e) => handleInputChange("mediaAssetReference", e.target.value)}
                fullWidth
              />
              <TextField
                label="Intended Gap"
                value={formData.intendedGap}
                onChange={(e) => handleInputChange("intendedGap", e.target.value)}
                fullWidth
              />
              <TextField
                label="Min Gap"
                value={formData.minGap}
                onChange={(e) => handleInputChange("minGap", e.target.value)}
                fullWidth
              />
              <TextField
                label="Compliant"
                value={formData.compliant}
                onChange={(e) => handleInputChange("compliant", e.target.value)}
                fullWidth
              />
              <TextField
                label="Moderate"
                value={formData.moderate}
                onChange={(e) => handleInputChange("moderate", e.target.value)}
                fullWidth
              />
              <TextField
                label="Non-Compliant"
                value={formData.nonCompliant}
                onChange={(e) => handleInputChange("nonCompliant", e.target.value)}
                fullWidth
              />
            </Box>
            <TextField
              label="Session Unlock Conditions"
              value={formData.unlockConditions}
              onChange={(e) => handleInputChange("unlockConditions", e.target.value)}
              fullWidth
              multiline
              rows={2}
              sx={{ mt: 2 }}
              required
            />
            <TextField
              label="Completion Criteria"
              value={formData.completionCriteria}
              onChange={(e) => handleInputChange("completionCriteria", e.target.value)}
              fullWidth
              multiline
              rows={2}
              sx={{ mt: 2 }}
              required
            />
            <TextField
              label="Session Summary Instruction"
              value={formData.summaryInstruction}
              onChange={(e) => handleInputChange("summaryInstruction", e.target.value)}
              fullWidth
              multiline
              rows={3}
              sx={{ mt: 2 }}
              required
            />
            <TextField
              label="Prompt"
              value={formData.prompt}
              onChange={(e) => handleInputChange("prompt", e.target.value)}
              fullWidth
              multiline
              rows={3}
              sx={{ mt: 2 }}
              placeholder="Enter prompt content..."
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={closeModal}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!formData.sessionName || !formData.sessionNumber || !formData.unlockConditions || !formData.completionCriteria || !formData.summaryInstruction}
            >
              {editingSession ? "Update" : "Create"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* View Modal */}
        <Dialog open={viewModal} onClose={() => setViewModal(false)} maxWidth="md" fullWidth>
          <DialogTitle>Session Details</DialogTitle>
          <DialogContent>
            {viewingSession && (
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mb: 2 }}>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">Session Name</Typography>
                    <Typography variant="body1">{viewingSession.sessionName}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">Session Number</Typography>
                    <Typography variant="body1">{viewingSession.sessionNumber}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">Media Asset Reference</Typography>
                    <Typography variant="body1">{viewingSession.mediaAssetReference}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                    <Chip 
                      label={viewingSession.isEnabled ? "Enabled" : "Disabled"} 
                      color={viewingSession.isEnabled ? "success" : "error"} 
                      size="small" 
                    />
                  </Box>
                </Box>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary">Session Unlock Conditions</Typography>
                  <Typography variant="body1">{viewingSession.unlockConditions}</Typography>
                </Box>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary">Completion Criteria</Typography>
                  <Typography variant="body1">{viewingSession.completionCriteria}</Typography>
                </Box>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary">Session Summary Instruction</Typography>
                  <Typography variant="body1">{viewingSession.summaryInstruction}</Typography>
                </Box>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary">Prompt</Typography>
                  <Box 
                    sx={{ 
                      maxHeight: 300, 
                      overflow: 'auto', 
                      border: '1px solid #e0e0e0', 
                      borderRadius: 1, 
                      p: 2,
                      backgroundColor: '#f9f9f9'
                    }}
                  >
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        fontFamily: 'monospace',
                        fontSize: '0.875rem'
                      }}
                    >
                      {viewingSession.prompt || "No prompt content"}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 2 }}>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">Intended Gap</Typography>
                    <Typography variant="body1">{viewingSession.intendedGap}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">Min Gap</Typography>
                    <Typography variant="body1">{viewingSession.minGap}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">Compliant</Typography>
                    <Typography variant="body1">{viewingSession.compliant}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">Moderate</Typography>
                    <Typography variant="body1">{viewingSession.moderate}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">Non-Compliant</Typography>
                    <Typography variant="body1">{viewingSession.nonCompliant}</Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewModal(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Prompt View Modal */}
        <Dialog open={promptModal} onClose={() => setPromptModal(false)} maxWidth="md" fullWidth>
          <DialogTitle>Prompt Content</DialogTitle>
          <DialogContent>
            <Box 
              sx={{ 
                maxHeight: 500, 
                overflow: 'auto', 
                border: '1px solid #e0e0e0', 
                borderRadius: 1, 
                p: 2,
                backgroundColor: '#f9f9f9',
                mt: 1
              }}
            >
              <Typography 
                variant="body1" 
                sx={{ 
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                  lineHeight: 1.6
                }}
              >
                {viewingPrompt}
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPromptModal(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
};

export default PromptManagement; 